'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { ChevronDown, ChevronUp, Compass, Search, Sparkles, Activity, Brain, CheckSquare, Settings, DollarSign, GitBranch, Globe, Puzzle, BookOpen, Video, Bot, Users } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useLlmConversations } from '@/hooks/useLlmConversations';
import { useLlmChat } from '@/hooks/useLlmChat';
import { useWorkspace } from '@/contexts/WorkspaceProvider';
import { buildCommandRegistry, CommandEntry } from './command-registry';
import { navViewerPersona } from '@/constants/nav';
import { AiResponse } from './AiResponse';
import { GoToIcon, AskAiIcon } from './icons';
import { useIsMobile } from '@/hooks/useIsMobile';
import { cn } from '@/lib/utils';

/** Number of page cards shown in collapsed grid before "View more" */
const GRID_DEFAULT_VISIBLE = 6;

/** Fallback icons for extra nav entries that have no custom icon (keyed by href) */
const FALLBACK_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  '/analytics/activity': Activity,
  '/ai-seo': Brain,
  '/tasks': CheckSquare,
  '/settings': Settings,
  '/analytics/revenue': DollarSign,
  '/workflows': GitBranch,
  '/workflows/builder': GitBranch,
  '/prospects': Users,
  '/vendor-leads': Globe,
  '/agents': Bot,
  '/internal-agents': Bot,
  '/integrations': Puzzle,
  '/playbooks': BookOpen,
  '/heygen': Video,
};

/** Number of page card rows visible when grid is expanded (3 cols x 3 rows) */
const GRID_EXPANDED_VISIBLE_ROWS = 3;

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const router = useRouter();
  const workspaceCtx = useWorkspace();
  const isMobile = useIsMobile();

  // ── Tab: "pages" (Go to Pages) or "ai" (Ask AI chat) ──
  const [activeTab, setActiveTab] = useState<'pages' | 'ai'>('pages');

  // ── Search query (applies to both tabs in pages mode) ──
  const [query, setQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  // ── Page grid expand / collapse ──
  const [gridExpanded, setGridExpanded] = useState(false);

  // ── AI chat state ──
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [aiInputValue, setAiInputValue] = useState('');
  const [aiPending, setAiPending] = useState(false);
  const [pendingQuestion, setPendingQuestion] = useState('');
  const aiInputRef = useRef<HTMLInputElement>(null);

  const { createConversation } = useLlmConversations({ createSource: 'command_palette' });

  // ── Build command registry ──
  const commands = useMemo(() => {
    return buildCommandRegistry({
      hasWorkspace: !!workspaceCtx?.activeWorkspace,
      subscriptionTier: workspaceCtx?.subscriptionTier ?? null,
      hasAccessToTier: workspaceCtx?.hasAccessToTier ?? (() => true),
      activeWorkspace: workspaceCtx?.activeWorkspace ?? null,
      viewerPersona: navViewerPersona(workspaceCtx),
    });
  }, [workspaceCtx]);

  const pageCommands = useMemo(
    () => commands.filter((c) => c.category === 'Go To'),
    [commands],
  );
  const aiSuggestions = useMemo(
    () => commands.filter((c) => c.category === 'Ask AI'),
    [commands],
  );

  // ── Build AI context payload ──
  const commandContext = useMemo(() => ({
    available_commands: commands.map((c) => ({
      label: c.label,
      category: c.category,
      description: c.description,
      href: c.action.type === 'navigate' ? c.action.href : undefined,
    })),
  }), [commands]);

  const { messages, isStreaming, error, sendMessage, clearMessages } = useLlmChat({
    context: commandContext,
  });

  // ── Filtered page commands when searching ──
  const isSearching = query.trim().length > 0;
  const filteredPages = useMemo(() => {
    if (!isSearching) return pageCommands;
    const q = query.toLowerCase();
    return commands.filter(
      (c) =>
        c.label.toLowerCase().includes(q) ||
        c.keywords.some((k) => k.includes(q)) ||
        c.description?.toLowerCase().includes(q),
    );
  }, [commands, pageCommands, isSearching, query]);

  // ── Reset when dialog closes ──
  const handleOpenChange = useCallback((newOpen: boolean) => {
    if (!newOpen) {
      setQuery('');
      setActiveTab('pages');
      setGridExpanded(false);
    }
    onOpenChange(newOpen);
  }, [onOpenChange]);

  // ── Focus search on open ──
  useEffect(() => {
    if (open) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [open]);

  // ── Focus AI input when switching to AI tab ──
  useEffect(() => {
    if (activeTab === 'ai') {
      setTimeout(() => aiInputRef.current?.focus(), 100);
    }
  }, [activeTab]);

  // ── Handlers ──
  const navigateTo = useCallback(
    (href: string) => {
      handleOpenChange(false);
      router.push(href);
    },
    [router, handleOpenChange],
  );

  const startAiChat = useCallback(
    async (question: string) => {
      setActiveTab('ai');
      clearMessages();
      setPendingQuestion(question);
      setAiPending(true);
      const conv = await createConversation('Command Palette');
      if (!conv) { setAiPending(false); setPendingQuestion(''); return; }
      setConversationId(conv.id);
      setAiPending(false);
      setPendingQuestion('');
      await sendMessage(conv.id, question);
    },
    [createConversation, sendMessage, clearMessages],
  );

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        if (query) { setQuery(''); return; }
        handleOpenChange(false);
      }
      if (e.key === 'Enter' && isSearching) {
        const q = query.trim();
        if (q.length > 5 && (q.includes('?') || q.split(' ').length >= 3)) {
          e.preventDefault();
          startAiChat(q);
        }
      }
    },
    [query, isSearching, handleOpenChange, startAiChat],
  );

  const handleAiSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const val = aiInputValue.trim();
      if (!val || isStreaming) return;
      setAiInputValue('');
      if (conversationId) {
        await sendMessage(conversationId, val);
      } else {
        setPendingQuestion(val);
        setAiPending(true);
        const conv = await createConversation('Command Palette');
        if (!conv) { setAiPending(false); setPendingQuestion(''); return; }
        setConversationId(conv.id);
        setAiPending(false);
        setPendingQuestion('');
        await sendMessage(conv.id, val);
      }
    },
    [aiInputValue, conversationId, isStreaming, sendMessage, createConversation],
  );

  const resetAiChat = useCallback(() => {
    setConversationId(null);
    setAiInputValue('');
    setAiPending(false);
    setPendingQuestion('');
    clearMessages();
  }, [clearMessages]);

  const handleAiNavigation = useCallback(
    (href: string) => { handleOpenChange(false); router.push(href); },
    [router, handleOpenChange],
  );

  // ── Visible page cards (grid mode: 6 default, expand to show all) ──
  const hasMoreCards = pageCommands.length > GRID_DEFAULT_VISIBLE;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(
          'flex flex-col overflow-hidden p-0 shadow-2xl max-w-[680px] w-[92vw] gap-0 command-palette-dialog rounded-[26px] sm:rounded-[26px] border border-gray-100',
          isMobile ? 'max-h-[min(80vh,calc(100dvh-2rem))] h-auto' : 'h-[676px] max-h-[80vh]',
        )}
        showCloseButton={isMobile}
      >
        <DialogHeader className="mx-0 mt-0">
          <VisuallyHidden asChild>
            <DialogTitle>Command Palette</DialogTitle>
          </VisuallyHidden>
        </DialogHeader>

        {/* ── Search bar ── */}
        <div className="flex items-center gap-3 px-5 py-3.5 border-b border-gray-100 flex-shrink-0">
          <Search className="w-5 h-5 text-gray-400 flex-shrink-0" />
          <input
            ref={searchInputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search for pages, settings or ask AI..."
            className="flex-1 text-base bg-transparent outline-none placeholder:text-gray-400 text-gray-900"
          />
        </div>

        {/* ── Tab bar ── */}
        <div className="grid grid-cols-2 border-b border-gray-100 flex-shrink-0">
          <button
            onClick={() => setActiveTab('pages')}
            className={`flex items-center justify-center gap-2 py-3 text-base transition-colors relative ${
              activeTab === 'pages'
                ? 'text-gray-900'
                : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            <GoToIcon className="w-4 h-4" />
            Go to Pages
            {activeTab === 'pages' && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900 rounded-full" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('ai')}
            className={`flex items-center justify-center gap-2 py-3 text-base transition-colors relative ${
              activeTab === 'ai'
                ? 'text-gray-900'
                : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            <AskAiIcon className="w-4 h-4" />
            Ask AI
            {activeTab === 'ai' && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900 rounded-full" />
            )}
          </button>
        </div>

        {/* ── Content ── */}
        {activeTab === 'pages' ? (
          <div className="flex-1 min-h-0 overflow-hidden">
            {isSearching ? (
              /* ── Search results (flat list) ── */
              <div className="py-2 overflow-y-auto h-full">
                {filteredPages.length === 0 ? (
                  <div className="py-6 text-center text-sm text-gray-400">
                    No results for &ldquo;{query}&rdquo;
                    {query.length > 2 && (
                      <button
                        className="mt-2 flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 mx-auto"
                        onClick={() => startAiChat(query.trim())}
                      >
                        <Sparkles className="w-4 h-4" />
                        Ask AI: &ldquo;{query.trim()}&rdquo;
                      </button>
                    )}
                  </div>
                ) : (
                  filteredPages.map((cmd) => (
                    <SearchResultItem key={cmd.id} cmd={cmd} onNavigate={navigateTo} onAskAi={startAiChat} />
                  ))
                )}
              </div>
            ) : (
              /* ── Default: ASK AI suggestions + GO TO PAGES grid ── */
              <div className="flex flex-col h-full">
                {/* ASK AI section — always visible */}
                {aiSuggestions.length > 0 && (
                  <section className="flex-shrink-0 px-5 pt-5 pb-3">
                    <p className="text-sm uppercase tracking-wider text-gray-400 mb-3">Ask AI</p>
                    <div className="space-y-1">
                      {aiSuggestions.slice(0, 3).map((cmd) => (
                        <button
                          key={cmd.id}
                          onClick={() => cmd.action.type === 'ask_ai' && startAiChat(cmd.action.query)}
                          className="flex items-center gap-3 w-full px-2.5 py-2.5 rounded-lg hover:bg-gray-50 text-left transition-colors group"
                        >
                          <Sparkles className="w-5 h-5 text-gray-400 flex-shrink-0 group-hover:text-blue-500 transition-colors" />
                          <span className="text-base text-gray-900">{cmd.label}</span>
                        </button>
                      ))}
                    </div>
                  </section>
                )}

                <div className="h-px bg-gray-100 mx-5 flex-shrink-0" />

                {/* GO TO PAGES section — scrollable */}
                <section className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
                  <p className="text-sm uppercase tracking-wider text-gray-400 mb-3">Go to Pages</p>
                  <div className="grid grid-cols-3 gap-3">
                    {(gridExpanded ? pageCommands : pageCommands.slice(0, GRID_DEFAULT_VISIBLE)).map((cmd) => (
                      <PageCard key={cmd.id} cmd={cmd} onClick={() => navigateTo((cmd.action as { type: 'navigate'; href: string }).href)} />
                    ))}
                  </div>

                  {hasMoreCards && (
                    <button
                      onClick={() => setGridExpanded((p) => !p)}
                      className="mt-3 flex items-center gap-1.5 mx-auto text-base text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {gridExpanded ? (
                        <><span>View less</span><ChevronUp className="w-4 h-4" /></>
                      ) : (
                        <><span>View more</span><ChevronDown className="w-4 h-4" /></>
                      )}
                    </button>
                  )}
                </section>
              </div>
            )}
          </div>
        ) : (
          /* ── AI tab ── */
          <div className="flex flex-col flex-1 min-h-0">
            {/* Before chat: show AI suggestion prompts */}
            {messages.length === 0 && !isStreaming && !aiPending ? (
              <div className="flex-1 overflow-y-auto px-5 py-5">
                <p className="text-sm uppercase tracking-wider text-gray-400 mb-3">Ask AI</p>
                <div className="space-y-1">
                  {aiSuggestions.map((cmd) => (
                    <button
                      key={cmd.id}
                      onClick={() => cmd.action.type === 'ask_ai' && startAiChat(cmd.action.query)}
                      className="flex items-center gap-3 w-full px-2.5 py-2.5 rounded-lg hover:bg-gray-50 text-left transition-colors group"
                    >
                      <Sparkles className="w-5 h-5 text-gray-400 flex-shrink-0 group-hover:text-blue-500 transition-colors" />
                      <span className="text-base text-gray-900">{cmd.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <AiResponse
                messages={messages}
                isStreaming={isStreaming}
                isPending={aiPending}
                pendingQuestion={pendingQuestion}
                error={error}
                onNavigate={handleAiNavigation}
                onBack={() => { resetAiChat(); setTimeout(() => aiInputRef.current?.focus(), 100); }}
              />
            )}

            <form onSubmit={handleAiSubmit} className="border-t border-gray-100 px-4 py-3.5 flex items-center gap-3 flex-shrink-0">
              <input
                ref={aiInputRef}
                type="text"
                value={aiInputValue}
                onChange={(e) => setAiInputValue(e.target.value)}
                placeholder={conversationId ? 'Ask follow up question...' : 'Ask a question...'}
                className="flex-1 text-base bg-transparent outline-none placeholder:text-gray-400"
                disabled={isStreaming}
              />
              <button
                type="submit"
                disabled={isStreaming || !aiInputValue.trim()}
                className="flex-shrink-0 transition-opacity disabled:opacity-30"
              >
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="32" height="32" rx="7.2" fill="#F2F5FF" />
                  <path d="M8.008 23.2008L24.8 16.0008L8.008 8.80078L8 14.4008L20 16.0008L8 17.6008L8.008 23.2008Z" fill="#094FFE" />
                </svg>
              </button>
            </form>
          </div>
        )}

        {/* ── Footer (pages tab only) ── */}
        {activeTab === 'pages' && (
          <div className="border-t border-gray-100 px-5 py-2.5 flex items-center gap-5 text-sm text-gray-400 flex-shrink-0">
            <span className="flex items-center gap-1.5">
              <kbd className="px-1.5 py-0.5 rounded border border-gray-200 bg-gray-50 font-mono text-xs">esc</kbd>
              close
            </span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   Sub-components
   ──────────────────────────────────────────────────────────────────────────── */

function PageCard({ cmd, onClick }: { cmd: CommandEntry; onClick: () => void }) {
  const Icon = cmd.icon;
  const href = cmd.action.type === 'navigate' ? cmd.action.href : '';
  const FallbackIcon = FALLBACK_ICONS[href] || Compass;
  return (
    <button
      onClick={onClick}
      className="flex items-start gap-3 p-3.5 rounded-lg border border-gray-100 hover:border-gray-200 hover:bg-gray-50 text-left transition-colors w-full"
    >
      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center mt-0.5">
        {Icon ? <Icon className="w-5 h-5 text-black" /> : <FallbackIcon className="w-5 h-5 text-black" />}
      </div>
      <div className="min-w-0">
        <p className="text-base font-medium text-gray-900 leading-snug">{cmd.label}</p>
        {cmd.shortDescription && (
          <p className="text-sm text-gray-400 mt-0.5 leading-snug">{cmd.shortDescription}</p>
        )}
      </div>
    </button>
  );
}

function SearchResultItem({
  cmd,
  onNavigate,
  onAskAi,
}: {
  cmd: CommandEntry;
  onNavigate: (href: string) => void;
  onAskAi: (query: string) => void;
}) {
  const Icon = cmd.icon;
  const handleClick = () => {
    if (cmd.action.type === 'navigate') onNavigate(cmd.action.href);
    else if (cmd.action.type === 'ask_ai') onAskAi(cmd.action.query);
  };

  return (
    <button
      onClick={handleClick}
      className="flex items-center gap-3 w-full px-5 py-3 hover:bg-gray-50 text-left transition-colors group"
    >
      <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
        {cmd.category === 'Ask AI' ? (
          <Sparkles className="w-5 h-5 text-gray-500 group-hover:text-blue-500 transition-colors" />
        ) : Icon ? (
          <Icon className="w-5 h-5 text-gray-500" />
        ) : (
          <Compass className="w-5 h-5 text-gray-400" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-base font-medium text-gray-900">{cmd.label}</p>
        {cmd.description && (
          <p className="text-sm text-gray-400 truncate">{cmd.description}</p>
        )}
      </div>
      <span className="text-sm text-gray-300 flex-shrink-0">{cmd.category}</span>
    </button>
  );
}
